/**
 * LawBillity Time Dashboard
 * Directors see all fee earners; individual fee earners see only their own.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { ArrowLeft, Clock, Users, Briefcase, RefreshCw } from "lucide-react";

import { API } from '../../config/api';

export default function TimeDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");
  const [feeEarner, setFeeEarner] = useState("");
  const [matterSearch, setMatterSearch] = useState("");

  const userId = localStorage.getItem("bonsai_user_id");

  useEffect(() => {
    if (!userId) { window.location.href = "/login"; }
  }, [userId]);

  async function fetchData() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (feeEarner) params.set("fee_earner", feeEarner);
      if (matterSearch) params.set("matter", matterSearch);
      const resp = await fetch(`${API}/bonsai/time/dashboard?${params}`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      const d = await resp.json();
      setData(d);
    } catch (e) {
      console.error("Failed to fetch time dashboard:", e);
    }
    setLoading(false);
  }

  useEffect(() => { fetchData(); }, [days, feeEarner]);

  const isDirector = data?.is_director || false;
  const byUser = data?.by_user || [];
  const byMatter = data?.by_matter || [];
  const entries = data?.entries || [];
  const maxUserHours = Math.max(...byUser.map(u => u.hours), 0.1);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="time-dashboard-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold" data-testid="time-dashboard-title">
                {isDirector ? "LawBillity — Firm Time" : "LawBillity — My Time"}
              </h1>
              <p className="text-sm text-gray-400">
                {isDirector ? "All fee earner time entries" : "Your recorded time entries"}
              </p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {isDirector && (
              <Badge variant="outline" className="text-purple-400 border-purple-700 text-xs" data-testid="director-badge">
                <Users className="w-3 h-3 mr-1" /> Director view
              </Badge>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="time-filters">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 bg-gray-900 border-gray-700 text-sm h-8" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          {isDirector && (
            <Select value={feeEarner} onValueChange={(v) => setFeeEarner(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 bg-gray-900 border-gray-700 text-sm h-8" data-testid="user-filter"><SelectValue placeholder="All fee earners" /></SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="all">All fee earners</SelectItem>
                {byUser.map(u => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-1">
            <Input
              placeholder="Search matter..."
              value={matterSearch}
              onChange={(e) => setMatterSearch(e.target.value)}
              className="w-40 bg-gray-900 border-gray-700 text-sm h-8"
              data-testid="matter-search"
            />
            <Button variant="outline" size="sm" onClick={fetchData} className="border-gray-700 h-8 px-2" data-testid="search-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <Clock className="w-5 h-5 text-purple-400 mx-auto mb-1" />
              <div className="text-3xl font-bold text-purple-400" data-testid="total-hours">{data?.total_hours || 0}h</div>
              <div className="text-xs text-gray-400">Total hours</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <Briefcase className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <div className="text-3xl font-bold text-blue-400" data-testid="total-entries">{data?.total_entries || 0}</div>
              <div className="text-xs text-gray-400">Time entries</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-teal mx-auto mb-1" />
              <div className="text-3xl font-bold text-teal" data-testid="matter-count">{byMatter.length}</div>
              <div className="text-xs text-gray-400">Matters</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* By User (only meaningful for directors) */}
          {isDirector && byUser.length > 0 && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-gray-300">Hours by Fee Earner</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                <table className="w-full text-xs" data-testid="user-hours-table">
                  <thead><tr className="text-gray-500 border-b border-gray-800"><th className="px-4 py-1 text-left">Name</th><th className="px-4 py-1 text-right">Hours</th><th className="px-4 py-1 text-right">Entries</th><th className="px-4 py-1 w-24"></th></tr></thead>
                  <tbody>
                    {byUser.map((u) => (
                      <tr key={u.email} className="border-b border-gray-800/40 hover:bg-gray-800/30 cursor-pointer" onClick={() => setFeeEarner(feeEarner === u.email ? "" : u.email)}>
                        <td className="px-4 py-2"><span className="font-bold text-gray-400 mr-1">{u.initials}</span><span className="text-white">{u.name}</span></td>
                        <td className="px-4 py-2 text-right font-bold text-purple-300">{u.hours}h</td>
                        <td className="px-4 py-2 text-right text-gray-400">{u.entries}</td>
                        <td className="px-4 py-2">
                          <div className="w-full h-1.5 bg-gray-800 rounded-md overflow-hidden">
                            <div className="h-full rounded-md bg-purple-500 transition-all" style={{ width: `${Math.round((u.hours / maxUserHours) * 100)}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* By Matter */}
          {byMatter.length > 0 && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-gray-300">Top Matters</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                <table className="w-full text-xs" data-testid="matter-hours-table">
                  <thead><tr className="text-gray-500 border-b border-gray-800"><th className="px-4 py-1 text-left">Matter</th><th className="px-4 py-1 text-right">Hours</th><th className="px-4 py-1 text-right">Entries</th></tr></thead>
                  <tbody>
                    {byMatter.map((m) => (
                      <tr key={m.matter_reference} className="border-b border-gray-800/40">
                        <td className="px-4 py-2 font-mono text-white">{m.matter_reference}</td>
                        <td className="px-4 py-2 text-right font-bold text-blue-300">{m.hours}h</td>
                        <td className="px-4 py-2 text-right text-gray-400">{m.entries}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Entries */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-gray-300">Recent Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-gray-500 text-sm">Loading...</div>
            ) : entries.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">No time entries in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="entries-table">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-500">
                      <th className="px-4 py-2 text-left">Date</th>
                      {isDirector && <th className="px-4 py-2 text-left">User</th>}
                      <th className="px-4 py-2 text-left">Matter</th>
                      <th className="px-4 py-2 text-right">Hours</th>
                      <th className="px-4 py-2 text-left">Narrative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i} className="border-b border-gray-800/40 hover:bg-gray-800/20" data-testid="entry-row">
                        <td className="px-4 py-2 text-gray-300 font-mono whitespace-nowrap">{e.date}</td>
                        {isDirector && <td className="px-4 py-2 text-gray-400">{e.user_id}</td>}
                        <td className="px-4 py-2 text-white font-mono">{e.matter_reference}</td>
                        <td className="px-4 py-2 text-right font-bold text-purple-300">{e.duration}h</td>
                        <td className="px-4 py-2 text-gray-400 max-w-xs truncate">{e.narrative}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="text-xs text-gray-500 mt-2 text-right">Last {days} days</div>
      </div>
    </div>
  );
}
